import { useEffect, useState } from "react";
import { Search, ShieldCheck, ShieldOff, Crown, User } from "lucide-react";
import { toast } from "sonner";
import { api, asError } from "@/api/client";

export default function AdminUsers() {
  const [users, setUsers] = useState([]);
  const [hasMore, setHasMore] = useState(false);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [search, setSearch] = useState("");

  const load = (reset = true) => {
    if (reset) { setLoading(true); setPage(1); }
    const nextPage = reset ? 1 : page + 1;
    const params = { page: nextPage, limit: 50 };
    if (search) params.search = search;
    api.get("/admin/users", { params })
      .then(({ data }) => {
        const items = data.items || data;
        setUsers((prev) => reset ? items : [...prev, ...items]);
        setHasMore(!!data.has_more);
        if (!reset) setPage(nextPage);
      })
      .catch(() => toast.error("Failed to load users"))
      .finally(() => { setLoading(false); setLoadingMore(false); });
  };
  useEffect(() => { const t = setTimeout(() => load(true), 250); return () => clearTimeout(t); /* eslint-disable-next-line */ }, [search]);

  const loadMore = () => { if (loadingMore || !hasMore) return; setLoadingMore(true); load(false); };

  const toggleDisabled = async (u) => {
    try {
      const { data } = await api.patch(`/admin/users/${u.id}`, { disabled: !u.disabled });
      setUsers((arr) => arr.map((x) => (x.id === u.id ? { ...x, ...data } : x)));
      toast.success(data.disabled ? "User disabled" : "User enabled");
    } catch (e) { toast.error(asError(e)); }
  };

  const fmt = (iso) => iso ? new Date(iso).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" }) : "—";

  return (
    <div data-testid="admin-users">
      <div className="relative max-w-md mb-5">
        <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--ink-3)]" />
        <input
          data-testid="admin-users-search"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search by name or email…"
          className="w-full pl-9 pr-3 py-2 text-sm bg-white border border-[#e5dec9] rounded-md focus:border-[var(--green)] outline-none"
        />
      </div>

      <div className="bg-white border border-[#e5dec9] rounded-xl overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-[var(--bg-cream)] text-[var(--ink-3)] uppercase text-[10px] tracking-widest">
            <tr>
              <th className="text-left px-4 py-3 w-12">S.No</th>
              <th className="text-left">User</th>
              <th className="text-left">Role</th>
              <th className="text-left">Joined</th>
              <th className="text-right">Orders</th>
              <th className="text-right">Lifetime Spend</th>
              <th className="text-center">Status</th>
              <th className="text-right px-4">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan={8} className="text-center py-10 text-[var(--ink-3)] kbd text-xs">Loading…</td></tr>}
            {!loading && users.length === 0 && <tr><td colSpan={8} className="text-center py-12 text-[var(--ink-3)] text-sm">No users found.</td></tr>}
            {users.map((u, idx) => (
              <tr key={u.id} data-testid={`admin-user-row-${u.id}`} className="border-t border-[#f0e7d3] hover:bg-[var(--bg-cream)]/40">
                <td className="px-4 py-3 kbd text-[0.7rem] text-[var(--ink-3)]">{idx + 1}</td>
                <td className="py-3">
                  <div className="flex items-center gap-3">
                    <div className={`w-9 h-9 rounded-full flex items-center justify-center text-[11px] font-bold ${u.role === "admin" ? "bg-[var(--gold)] text-[var(--green-deep)]" : "bg-[var(--green)] text-white"}`}>
                      {(u.name || u.email)[0]?.toUpperCase()}
                    </div>
                    <div>
                      <div className="font-semibold text-[var(--ink)]">{u.name || "—"}</div>
                      <div className="text-[var(--ink-3)] text-xs">{u.email}</div>
                    </div>
                  </div>
                </td>
                <td>
                  {u.role === "admin"
                    ? <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest font-semibold text-[var(--gold)]"><Crown size={11} /> Admin</span>
                    : <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest font-semibold text-[var(--ink-3)]"><User size={11} /> Customer</span>}
                </td>
                <td className="text-[var(--ink-2)] text-xs">{fmt(u.created_at)}</td>
                <td className="text-right font-semibold text-[var(--ink)]">{u.order_count || 0}</td>
                <td className="text-right font-semibold text-[var(--green)]">₹{(u.lifetime_spend || 0).toLocaleString("en-IN")}</td>
                <td className="text-center">
                  {u.disabled
                    ? <span className="text-[10px] uppercase tracking-widest font-semibold text-red-700 bg-red-50 border border-red-200 rounded-full px-2 py-1">Disabled</span>
                    : <span className="text-[10px] uppercase tracking-widest font-semibold text-[var(--green)] bg-green-50 border border-green-200 rounded-full px-2 py-1">Active</span>}
                </td>
                <td className="px-4 text-right">
                  {u.role !== "admin" && (
                    <button
                      onClick={() => toggleDisabled(u)}
                      data-testid={`admin-user-toggle-${u.id}`}
                      title={u.disabled ? "Enable user" : "Disable user"}
                      className="p-1.5 hover:bg-[var(--bg-cream)] rounded inline-flex items-center gap-1 text-xs"
                    >
                      {u.disabled
                        ? <><ShieldCheck size={13} className="text-[var(--green)]" /> Enable</>
                        : <><ShieldOff size={13} className="text-red-600" /> Disable</>}
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {hasMore && (
        <div className="flex justify-center mt-5">
          <button
            data-testid="admin-users-load-more"
            onClick={loadMore}
            disabled={loadingMore}
            className="btn-outline disabled:opacity-60"
          >
            {loadingMore ? "Loading…" : "Load More Users"}
          </button>
        </div>
      )}
    </div>
  );
}
